import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import GetSelfCheckoutDashboard from "../../hooks/fetch-hooks/get-self-checkout-dashboard/get-self-checkout-dashboard";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const today = new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

const CHART_COLORS = {
    purple:     "rgba(144, 43, 139, 1)",
    purpleLight:"rgba(144, 43, 139, 0.15)",
    blue:       "rgba(59, 130, 246, 1)",
    blueLight:  "rgba(59, 130, 246, 0.15)",
    green:      "rgba(34, 197, 94, 1)",
    greenLight: "rgba(34, 197, 94, 0.15)",
    orange:     "rgba(249, 115, 22, 1)",
    orangeLight:"rgba(249, 115, 22, 0.15)",
    red:        "rgba(239, 68, 68, 1)",
    redLight:   "rgba(239, 68, 68, 0.15)",
    yellow:     "rgba(234, 179, 8, 1)",
    yellowLight:"rgba(234, 179, 8, 0.15)",
    teal:       "rgba(20, 184, 166, 1)",
    tealLight:  "rgba(20, 184, 166, 0.15)",
};

const legendLabels = {
    color: "#374151",
    usePointStyle: true,
    pointStyleWidth: 16,
    padding: 20,
};

const chartOpts = (title) => ({
    responsive: true,
    plugins: {
        legend: { position: "bottom", labels: legendLabels },
        title: { display: !!title, text: title, color: "#374151" },
    },
    scales: { y: { beginAtZero: true, ticks: { color: "#6b7280" }, grid: { color: "#f3f4f6" } }, x: { ticks: { color: "#6b7280" } } },
});

const doughnutOpts = {
    responsive: true,
    plugins: { legend: { position: "bottom", labels: legendLabels } },
};

function StatCard({ label, value, sub }) {
    return (
        <div className="bg-white rounded-2xl shadow-soft-xl p-5 flex flex-col gap-1">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-2xl font-semibold text-gray-800">{value ?? "—"}</span>
            {sub && <span className="text-xs text-gray-400">{sub}</span>}
        </div>
    );
}

StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sub: PropTypes.string,
};

function formatDuration(totalSeconds) {
    if (totalSeconds == null || Number.isNaN(totalSeconds)) return "—";
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    const s = Math.floor(totalSeconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function Section({ title, children }) {
    return (
        <div className="flex flex-col gap-4">
            <h2 className="text-base font-semibold text-gray-700 border-b pb-2">{title}</h2>
            {children}
        </div>
    );
}

Section.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

function SelfCheckoutDashboard() {
    const [from, setFrom] = useState(thirtyDaysAgo);
    const [to, setTo] = useState(today);
    const [granularity, setGranularity] = useState("day");
    const [autocartPage, setAutocartPage] = useState(1);
    const autocartPageSize = 20;

    const { data: resp, isLoading, isError } = GetSelfCheckoutDashboard({ from, to, granularity, autocartPage, autocartPageSize });
    const d = resp?.data?.data;

    const labels = useMemo(() => {
        if (!d?.funnel?.series?.length) return [];
        const all = new Set();
        d.funnel.series.forEach(r => all.add(r.period));
        d.abandonment.series.forEach(r => all.add(r.period));
        d.paymentPath.series.forEach(r => all.add(r.period));
        return [...all].sort();
    }, [d]);

    const byPeriod = (series, field) => {
        const map = Object.fromEntries((series || []).map(r => [r.period, r[field] || 0]));
        return labels.map(l => map[l] ?? 0);
    };

    const funnelChartData = useMemo(() => ({
        labels,
        datasets: [
            { label: "Rx Uploaded",       data: byPeriod(d?.funnel?.series, "prescriptionUploaded"), borderColor: CHART_COLORS.purple,  backgroundColor: "transparent", pointBackgroundColor: CHART_COLORS.purple,  tension: 0.3 },
            { label: "Cart Created",       data: byPeriod(d?.funnel?.series, "cartCreated"),          borderColor: CHART_COLORS.blue,    backgroundColor: "transparent", pointBackgroundColor: CHART_COLORS.blue,    tension: 0.3 },
            { label: "Consent Given",      data: byPeriod(d?.funnel?.series, "consentGiven"),         borderColor: CHART_COLORS.teal,    backgroundColor: "transparent", pointBackgroundColor: CHART_COLORS.teal,    tension: 0.3 },
            { label: "Checkout Started",   data: byPeriod(d?.funnel?.series, "checkoutStarted"),      borderColor: CHART_COLORS.orange,  backgroundColor: "transparent", pointBackgroundColor: CHART_COLORS.orange,  tension: 0.3 },
            { label: "Payment Confirmed",  data: byPeriod(d?.funnel?.series, "paymentConfirmed"),     borderColor: CHART_COLORS.green,   backgroundColor: "transparent", pointBackgroundColor: CHART_COLORS.green,   tension: 0.3 },
        ],
    }), [d, labels]);

    const abandonChartData = useMemo(() => ({
        labels,
        datasets: [{ label: "Carts Abandoned", data: byPeriod(d?.abandonment?.series, "cartsAbandoned"), backgroundColor: CHART_COLORS.orange }],
    }), [d, labels]);

    const callSeriesChartData = useMemo(() => ({
        labels,
        datasets: [
            { label: "Attempted",           data: byPeriod(d?.abandonment?.callSeries, "callsAttempted"),     backgroundColor: CHART_COLORS.blue },
            { label: "Connected",           data: byPeriod(d?.abandonment?.callSeries, "callsConnected"),     backgroundColor: CHART_COLORS.teal },
            { label: "Proceeded to Payment",data: byPeriod(d?.abandonment?.callSeries, "proceededToPayment"), backgroundColor: CHART_COLORS.green },
            { label: "Lost",                data: byPeriod(d?.abandonment?.callSeries, "lost"),               backgroundColor: CHART_COLORS.red },
        ],
    }), [d, labels]);

    const paymentDoughnutData = useMemo(() => ({
        labels: ["No Substitution", "With Substitution"],
        datasets: [{
            data: [d?.paymentPath?.totals?.noSubstitution ?? 0, d?.paymentPath?.totals?.withSubstitution ?? 0],
            backgroundColor: [CHART_COLORS.green, CHART_COLORS.orange],
        }],
    }), [d]);

    const refundChartData = useMemo(() => ({
        labels,
        datasets: [
            { label: "Initiated",  data: byPeriod(d?.refunds?.series, "initiated"),  backgroundColor: CHART_COLORS.orange },
            { label: "Completed",  data: byPeriod(d?.refunds?.series, "completed"),  backgroundColor: CHART_COLORS.green  },
        ],
    }), [d, labels]);

    const excessChartData = useMemo(() => ({
        labels,
        datasets: [{ label: "Amount Absorbed (₹)", data: byPeriod(d?.excess?.series, "totalAmount"), backgroundColor: CHART_COLORS.purple }],
    }), [d, labels]);

    return (
        <div className="p-6 flex flex-col gap-8">
            <div className="flex flex-wrap items-center gap-4">
                <h1 className="text-xl font-semibold text-gray-800 flex-1">Self-Checkout Dashboard</h1>

                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">From</label>
                    <input type="date" value={from} max={to} onChange={e => { setFrom(e.target.value); setAutocartPage(1); }}
                        className="border rounded-lg px-3 py-1.5 text-sm" />
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">To</label>
                    <input type="date" value={to} min={from} max={today} onChange={e => { setTo(e.target.value); setAutocartPage(1); }}
                        className="border rounded-lg px-3 py-1.5 text-sm" />
                </div>

                <div className="flex rounded-lg overflow-hidden border text-sm">
                    {["day", "month"].map(g => (
                        <button key={g} onClick={() => setGranularity(g)}
                            className={`px-4 py-1.5 capitalize ${granularity === g ? "bg-[#902b8b] text-white-50" : "bg-white text-gray-600"}`}>
                            {g}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading && <div className="text-center text-gray-400 py-20">Loading...</div>}
            {isError && <div className="text-center text-red-400 py-20">Failed to load dashboard data.</div>}

            {d?.funnel && (
                <div className="flex flex-col gap-10">

                    {/* Section 1 — Acquisition funnel */}
                    <Section title="1. Acquisition Funnel">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <StatCard label="Rx Uploaded"      value={d.funnel.totals.prescriptionUploaded} />
                            <StatCard label="Cart Created"      value={d.funnel.totals.cartCreated} />
                            <StatCard label="Consent Given"     value={d.funnel.totals.consentGiven} />
                            <StatCard label="Checkout Started"  value={d.funnel.totals.checkoutStarted} />
                            <StatCard label="Payment Confirmed" value={d.funnel.totals.paymentConfirmed} />
                            <StatCard label="Conversion Rate"   value={`${d.funnel.totals.conversionRate}%`} sub="Rx → Payment" />
                        </div>
                        {labels.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                                <Line data={funnelChartData} options={chartOpts("")} />
                            </div>
                        )}
                    </Section>

                    {/* Section 2 — Cart abandonment */}
                    <Section title="2. Cart Abandonment Recovery">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <StatCard label="Carts Abandoned"      value={d.abandonment.totals.cartsAbandoned} />
                            <StatCard label="Calls Attempted"      value={d.abandonment.totals.callsAttempted} />
                            <StatCard label="Calls Connected"      value={d.abandonment.totals.callsConnected} />
                            <StatCard label="Proceeded to Payment" value={d.abandonment.totals.proceededToPayment} />
                            <StatCard label="Lost"                 value={d.abandonment.totals.lost} />
                        </div>
                        {d.abandonment.totals.notProceedReasons?.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                                <h3 className="text-sm font-medium text-gray-600 mb-3">Reason for Not Proceeding</h3>
                                <div className="flex flex-col gap-2">
                                    {d.abandonment.totals.notProceedReasons.map(({ reason, count }) => (
                                        <div key={reason} className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">{reason}</span>
                                            <span className="font-semibold text-gray-800">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {labels.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                                <Bar data={abandonChartData} options={chartOpts("Carts Abandoned per period")} />
                            </div>
                        )}
                        {labels.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                                <Bar data={callSeriesChartData} options={{ ...chartOpts("Call Outcomes per period"), scales: { ...chartOpts().scales, x: { stacked: false, ticks: { color: "#6b7280" } }, y: { stacked: false, beginAtZero: true, ticks: { color: "#6b7280" }, grid: { color: "#f3f4f6" } } } }} />
                            </div>
                        )}
                    </Section>

                    {/* Section 3 — Payment completion path */}
                    <Section title="3. Payment Completion Path">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <StatCard label="Total Payments Confirmed" value={d.paymentPath.totals.total} />
                            <StatCard label="No Substitution Needed"   value={d.paymentPath.totals.noSubstitution} />
                            <StatCard label="Required Substitution"    value={d.paymentPath.totals.withSubstitution} />
                        </div>
                        {d.paymentPath.totals.total > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5 max-w-xs mx-auto">
                                <Doughnut data={paymentDoughnutData} options={doughnutOpts} />
                            </div>
                        )}
                    </Section>

                    {/* Section 4 — Substitution outcomes */}
                    <Section title="4. Substitution Outcomes">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <StatCard label="Proposed (period)"  value={d.substitution.totals.proposed} />
                            <StatCard label="Agreed"             value={d.substitution.totals.agreed} />
                            <StatCard label="Did Not Agree"      value={d.substitution.totals.didNotAgree} />
                            <StatCard label="Pending (all-time)" value={d.substitution.totals.pendingAllTime} sub="Proposed with no resolution" />
                        </div>
                    </Section>

                    {/* Section 5 — Post-payment engagement */}
                    <Section title="5. Post-Payment Engagement">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <StatCard label="Entered My Rx Journey" value={d.postPayment.totals.myRxEntered} />
                        </div>
                    </Section>

                    {/* Section 6 — Refunds */}
                    <Section title="6. Refunds">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <StatCard label="Initiated (period)"    value={d.refunds.totals.initiated} />
                            <StatCard label="Pending (all-time)"    value={d.refunds.totals.pendingAllTime} sub="Initiated, not yet completed" />
                            <StatCard label="Completed (period)"    value={d.refunds.totals.completed} />
                        </div>
                        {labels.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                                <Bar data={refundChartData} options={chartOpts("Refunds per period")} />
                            </div>
                        )}
                    </Section>

                    {/* Section 7 — Autocart / Net TAT */}
                    {d.autocartTat && (
                        <Section title="7. Autocart & Net TAT">
                            {d.autocartTat.orders?.length > 0 ? (
                                <div className="bg-white rounded-2xl shadow-soft-xl p-5 overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead>
                                            <tr className="text-gray-500 border-b">
                                                <th className="pb-2 pr-6 font-medium">Order ID</th>
                                                <th className="pb-2 pr-6 font-medium">Cart ID</th>
                                                <th className="pb-2 pr-6 font-medium">Quotation ID</th>
                                                <th className="pb-2 pr-6 font-medium">Substitution</th>
                                                <th className="pb-2 font-medium text-right">Net TAT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {d.autocartTat.orders.map(o => (
                                                <tr key={o.orderId} className="border-b last:border-0">
                                                    <td className="py-2 pr-6 text-gray-700">#{o.orderId}</td>
                                                    <td className="py-2 pr-6 text-gray-500">#{o.cartId}</td>
                                                    <td className="py-2 pr-6 text-gray-500">#{o.quotationId}</td>
                                                    <td className="py-2 pr-6">
                                                        {o.substitutionCount > 0 ? (
                                                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Yes ({o.substitutionCount})</span>
                                                        ) : (
                                                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">No</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 text-right font-semibold text-purple-700">{formatDuration(o.netTatSeconds)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div className="flex items-center justify-between pt-4 mt-2 border-t">
                                        <span className="text-xs text-gray-500">
                                            {d.autocartTat.pagination.total === 0
                                                ? "0 orders"
                                                : `Showing ${(d.autocartTat.pagination.page - 1) * d.autocartTat.pagination.pageSize + 1}–${Math.min(d.autocartTat.pagination.page * d.autocartTat.pagination.pageSize, d.autocartTat.pagination.total)} of ${d.autocartTat.pagination.total}`}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                disabled={autocartPage <= 1}
                                                onClick={() => setAutocartPage(p => Math.max(1, p - 1))}
                                                className="px-3 py-1 text-sm rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed text-gray-600"
                                            >
                                                Prev
                                            </button>
                                            <span className="text-xs text-gray-500">
                                                Page {d.autocartTat.pagination.page} of {Math.max(1, Math.ceil(d.autocartTat.pagination.total / d.autocartTat.pagination.pageSize))}
                                            </span>
                                            <button
                                                disabled={autocartPage >= Math.ceil(d.autocartTat.pagination.total / d.autocartTat.pagination.pageSize)}
                                                onClick={() => setAutocartPage(p => p + 1)}
                                                className="px-3 py-1 text-sm rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed text-gray-600"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl shadow-soft-xl p-5 text-sm text-gray-400">No orders found for this date range.</div>
                            )}
                        </Section>
                    )}

                    {/* Section 8 — Excess absorbed */}
                    <Section title="8. Let-Go Money (Excess Absorbed)">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <StatCard label="Orders with Excess Absorbed" value={d.excess.totals.count} />
                            <StatCard label="Total Amount Absorbed"       value={`₹${d.excess.totals.totalAmount.toLocaleString("en-IN")}`} />
                        </div>
                        {labels.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                                <Bar data={excessChartData} options={chartOpts("Excess absorbed per period (₹)")} />
                            </div>
                        )}
                        {d.excess.orders?.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-soft-xl p-5 overflow-x-auto">
                                <h3 className="text-sm font-medium text-gray-600 mb-3">Per-Order Breakdown</h3>
                                <table className="w-full text-sm text-left">
                                    <thead>
                                        <tr className="text-gray-500 border-b">
                                            <th className="pb-2 pr-6 font-medium">Order ID</th>
                                            <th className="pb-2 pr-6 font-medium">Prescription ID</th>
                                            <th className="pb-2 pr-6 font-medium text-right">Paid</th>
                                            <th className="pb-2 pr-6 font-medium text-right">Invoiced</th>
                                            <th className="pb-2 font-medium text-right">Excess Absorbed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.excess.orders.map(o => (
                                            <tr key={o.orderId} className="border-b last:border-0">
                                                <td className="py-2 pr-6 text-gray-700">#{o.orderId}</td>
                                                <td className="py-2 pr-6 text-gray-500">{o.prescriptionId ? `#${o.prescriptionId}` : "—"}</td>
                                                <td className="py-2 pr-6 text-right text-gray-600">₹{o.paidAmount.toLocaleString("en-IN")}</td>
                                                <td className="py-2 pr-6 text-right text-gray-600">₹{o.invoicedAmount.toLocaleString("en-IN")}</td>
                                                <td className="py-2 text-right font-semibold text-purple-700">₹{o.excessAmount.toLocaleString("en-IN")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Section>

                </div>
            )}
        </div>
    );
}

export default SelfCheckoutDashboard;
